import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { brokers, type BrokerRequestDocumentItem } from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

import { DocumentsPanel } from '../documents/DocumentsPanel';

import { PERMISSAO_EDITAR } from './rotulos';

/**
 * Subaba "Documentos" do corretor: o painel comum de documentos (P-DOC) com o botao
 * "Pedir do catalogo", que cria as exigencias dos documentos que a seguradora pede as
 * corretoras (CreateBrokerRequestDocumentByInsurer do OnPoint), e o aviso dos
 * obrigatorios pendentes.
 *
 * O catalogo nao exige nada sozinho, e o pendente nao bloqueia a corretora: quais
 * documentos sao obrigatorios para operar e o que o pendente trava e regra pendente
 * (COR-002).
 */
export function BrokerDocumentsSection({ corretoraId }: { corretoraId: string }) {
  const { can } = useAuth();
  const podePedir = can(PERMISSAO_EDITAR) && can('documentos.documento.create');
  const [pedindo, setPedindo] = useState(false);

  const pendencias = useQuery({
    queryKey: ['broker', corretoraId, 'pending-documents'],
    queryFn: ({ signal }) => brokers.pendingDocuments(corretoraId, signal),
  });

  return (
    <div className="flex flex-col gap-4">
      {pendencias.data?.has_pending ? (
        <Alert tone="warning" title="Documentos obrigatorios pendentes">
          {pendencias.data.count === 1
            ? 'Ha 1 documento obrigatorio pedido a corretora e ainda nao recebido.'
            : `Ha ${pendencias.data.count} documentos obrigatorios pedidos a corretora e ainda nao recebidos.`}{' '}
          Nada e bloqueado: o efeito do pendente e regra ainda nao homologada.
        </Alert>
      ) : null}
      <DocumentsPanel
        ownerType="corretor"
        ownerId={corretoraId}
        title="Documentos do corretor"
        group="Corretor"
        allowDelete
        extraAction={
          podePedir ? (
            <Button variant="secondary" onClick={() => setPedindo(true)}>
              Pedir do catalogo
            </Button>
          ) : undefined
        }
      />
      {pedindo ? (
        <RequestFromCatalogDialog
          corretoraId={corretoraId}
          sugeridos={pendencias.data?.not_requested ?? []}
          onClose={() => setPedindo(false)}
        />
      ) : null}
    </div>
  );
}

function RequestFromCatalogDialog({
  corretoraId,
  sugeridos,
  onClose,
}: {
  corretoraId: string;
  sugeridos: BrokerRequestDocumentItem[];
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const catalogo = useQuery({
    queryKey: ['broker-request-documents', 'ativos'],
    queryFn: ({ signal }) => brokers.requestDocumentItems({ active: true }, signal),
  });
  const [escolhidos, setEscolhidos] = useState<string[]>(() =>
    sugeridos.map((item) => item.id),
  );

  const pedir = useMutation({
    mutationFn: () => brokers.requestDocuments(corretoraId, escolhidos),
    onSuccess: (criados) => {
      toast.success(
        criados.length === 0
          ? 'Os documentos escolhidos ja tinham sido pedidos.'
          : `${criados.length} documento(s) pedido(s) a corretora.`,
      );
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
      void queryClient.invalidateQueries({ queryKey: ['broker', corretoraId] });
      onClose();
    },
  });

  const itens = catalogo.data ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title="Pedir documentos do catalogo"
      description="Cada documento escolhido vira uma exigencia no repositorio, ligada ao item do catalogo. O que a corretora ja recebeu e pulado."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={pedir.isPending}
            disabled={escolhidos.length === 0}
            onClick={() => pedir.mutate()}
          >
            Pedir documentos
          </Button>
        </>
      }
    >
      {pedir.error ? (
        <Alert tone="danger" title="Nao foi possivel pedir">
          {pedir.error.message}
        </Alert>
      ) : null}
      {catalogo.isPending ? (
        <p className="text-sm text-ink-500">Carregando o catalogo.</p>
      ) : itens.length === 0 ? (
        <p className="text-sm text-ink-500">
          O catalogo de documentos pedidos ao corretor esta vazio. Ele e mantido em
          Configuracoes.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {itens.map((item) => (
            <li key={item.id}>
              <label className="flex items-start gap-2 text-sm text-ink-800">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={escolhidos.includes(item.id)}
                  onChange={(evento) =>
                    setEscolhidos((atual) =>
                      evento.target.checked
                        ? [...atual, item.id]
                        : atual.filter((escolhido) => escolhido !== item.id),
                    )
                  }
                />
                <span>
                  {item.name}
                  {item.required ? ' (obrigatorio)' : ''}
                  {item.description ? (
                    <span className="block text-2xs text-ink-500">
                      {item.description}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}
